import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  approveExpenseDraft,
  listExpenseCategories,
  listProperties,
  listPropertyUnits,
  rejectExpenseDraft,
  reopenExpenseDraft,
  unapproveExpenseDraft,
  updateExpenseDraft
} from './api';

const BROWSER_PREVIEWABLE = new Set(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']);
const AMOUNT_FIELDS = ['subtotal', 'taxTotal', 'total'];
const CURRENCIES = ['CAD', 'USD'];
const HINT_PROPS = { sx: { color: 'warning.dark', fontWeight: 500 } };

const FIELDS = [
  { name: 'vendorRawName', label: 'Vendor' },
  { name: 'transactionDate', label: 'Date', type: 'date' },
  { name: 'invoiceNumber', label: 'Invoice or receipt #' },
  { name: 'subtotal', label: 'Subtotal' },
  { name: 'taxTotal', label: 'Tax (GST, HST, PST)' },
  { name: 'total', label: 'Total' },
  { name: 'currency', label: 'Currency' },
  { name: 'description', label: 'What was it for?', multiline: true }
];
const PLACEMENT_FIELDS = ['propertyId', 'unitId', 'categoryId'];
const EDITABLE_FIELDS = [...FIELDS.map((f) => f.name), ...PLACEMENT_FIELDS];

const WARNING_TEXT = {
  amounts_do_not_add_up: 'The subtotal and tax we read don’t add up to the total. Please check the amounts.',
  not_an_expense_document: 'This doesn’t look like a receipt or bill. Please check the details.',
  implausible_date: 'The date we read didn’t look right, so we left it blank.',
  negative_amount: 'We left out a negative amount. Please enter the amounts yourself.'
};

const FAILED_TEXT = {
  unsupported_file_type: 'We can’t read this photo type automatically yet. Please type the details.',
  default: 'We couldn’t read this one. Type the details, or try reading it again.'
};

function toInputValue(value) {
  return value == null ? '' : String(value);
}

function initialValues(draft) {
  return Object.fromEntries(
    EDITABLE_FIELDS.map((name) => [name, toInputValue(draft?.[name] ?? (name === 'currency' ? 'CAD' : null))])
  );
}

function parseAmount(text) {
  const t = String(text || '').trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
}

/** Hint for values the reader was unsure of; disappears once the landlord changes the value. */
function confidenceHint(draft, name, value) {
  const read = draft?.extraction?.fields?.[name];
  if (!read || read.value == null) return null;
  const same = AMOUNT_FIELDS.includes(name)
    ? parseAmount(value) === Number(read.value)
    : String(value).trim() === String(read.value);
  if (!same) return null;
  if (read.confidence < 0.7) return 'Not sure we read this right. Please check.';
  if (read.confidence < 0.9) return 'Please check.';
  return null;
}

function money(n, currency) {
  try {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD' }).format(n);
  } catch (_) {
    return n.toFixed(2);
  }
}

function propertyLabel(property) {
  return property?.name || property?.address || 'Property';
}

function unitLabel(unit) {
  if (unit?.name) return unit.name;
  return unit?.unitNumber ? `Unit ${unit.unitNumber}` : 'Unit';
}

function formatDateTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Must match REJECTED_RECEIPT_RETENTION_DAYS on the backend. */
const REJECTED_RECEIPT_RETENTION_DAYS = 30;

function rejectedText(draft) {
  if (draft.filePurgedAt) {
    return `Marked as not an expense. The file and its details were deleted ${REJECTED_RECEIPT_RETENTION_DAYS} days later.`;
  }
  const rejectedAt = new Date(draft.rejectedAt);
  const deleteOn = Number.isNaN(rejectedAt.getTime())
    ? ''
    : formatDateTime(new Date(rejectedAt.getTime() + REJECTED_RECEIPT_RETENTION_DAYS * 24 * 60 * 60 * 1000).toISOString());
  return deleteOn
    ? `Marked as not an expense. It isn’t in your books. The file will be deleted on ${deleteOn} unless you move it back to review.`
    : 'Marked as not an expense. It isn’t in your books.';
}

/**
 * ZRF-EXP-CAP — review a receipt, then add it to the books or mark it as not an expense.
 * @param {{
 *   review: null | { phase: 'loading'|'reading'|'ready'|'error', doc?: object, error?: string, notice?: string },
 *   onClose: () => void,
 *   onRetry: () => void,
 *   onSaved: (draft: object, message: string) => void,
 *   onReopened: (draft: object, message: string) => void  // after an undo; the dialog stays open for edits
 * }} props
 */
function ExpenseDraftReview({ review, onClose, onRetry, onSaved, onReopened }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const doc = review?.doc;
  const draft = doc?.draft;
  const initial = useMemo(() => initialValues(draft), [draft]);
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [approveTried, setApproveTried] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [properties, setProperties] = useState(null);
  const [categories, setCategories] = useState(null);
  const [units, setUnits] = useState([]);
  const [optionsError, setOptionsError] = useState(null);

  const ready = review?.phase === 'ready' && draft;
  const locked = Boolean(ready) && draft.reviewStatus !== 'needs_review';

  useEffect(() => {
    setValues(initial);
    setActionError(null);
    setApproveTried(false);
    setConfirmReject(false);
    setConfirmRemove(false);
  }, [initial]);

  useEffect(() => {
    if (!ready || (properties && categories)) return undefined;
    let cancelled = false;
    Promise.all([listProperties(), listExpenseCategories()])
      .then(([props, cats]) => {
        if (cancelled) return;
        setProperties(Array.isArray(props) ? props : []);
        setCategories(Array.isArray(cats) ? cats : []);
        setOptionsError(null);
      })
      .catch(() => {
        if (!cancelled) setOptionsError('Could not load your properties and categories. Close and try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [ready, properties, categories]);

  useEffect(() => {
    setUnits([]);
    if (!values.propertyId) return undefined;
    let cancelled = false;
    listPropertyUnits(values.propertyId)
      .then((rows) => {
        if (!cancelled) setUnits(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [values.propertyId]);

  const amountErrors = Object.fromEntries(
    AMOUNT_FIELDS.map((name) => [name, Number.isNaN(parseAmount(values[name])) ? 'Enter an amount like 82.38' : null])
  );
  const [subtotal, tax, total] = AMOUNT_FIELDS.map((name) => parseAmount(values[name]));
  const mismatch =
    [subtotal, tax, total].every((n) => typeof n === 'number' && !Number.isNaN(n)) &&
    Math.abs(subtotal + tax - total) > 0.02;
  const missing = {
    transactionDate: !values.transactionDate && 'Enter the date',
    total: !(total > 0) && 'Enter the total',
    description: !values.description.trim() && 'Say what it was for',
    propertyId: !values.propertyId && 'Choose a property',
    categoryId: !values.categoryId && 'Choose a category'
  };
  const failed = doc?.status === 'failed';
  const warnings = (draft?.extraction?.warnings || []).filter((w) => WARNING_TEXT[w]);
  const category = categories?.find((c) => c.id === values.categoryId);
  const property = properties?.find((p) => p.id === values.propertyId);
  const unit = units.find((u) => u.id === values.unitId);

  const set = (name) => (event) =>
    setValues((prev) => ({
      ...prev,
      [name]: event.target.value,
      ...(name === 'propertyId' ? { unitId: '' } : {})
    }));

  const changes = () => {
    const patch = {};
    for (const name of EDITABLE_FIELDS) {
      if (values[name] === initial[name]) continue;
      const text = values[name].trim();
      if (text === '') patch[name] = null;
      else patch[name] = AMOUNT_FIELDS.includes(name) ? parseAmount(text) : text;
    }
    return patch;
  };

  const save = async () => {
    if (Object.values(amountErrors).some(Boolean)) return;
    const patch = changes();
    if (Object.keys(patch).length === 0) {
      onSaved(draft, 'Saved.');
      return;
    }
    setBusy('saving');
    setActionError(null);
    try {
      onSaved(await updateExpenseDraft(draft.id, patch), 'Saved.');
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Could not save your changes. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    setApproveTried(true);
    if (Object.values(amountErrors).some(Boolean) || Object.values(missing).some(Boolean) || mismatch) return;
    setBusy('approving');
    setActionError(null);
    try {
      const patch = changes();
      if (Object.keys(patch).length > 0) await updateExpenseDraft(draft.id, patch);
      const result = await approveExpenseDraft(draft.id);
      onSaved(result.draft, 'Added to your books.');
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Could not add this to your books. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    setBusy('rejecting');
    setActionError(null);
    try {
      onSaved(await rejectExpenseDraft(draft.id), 'Marked as not an expense.');
    } catch (err) {
      setConfirmReject(false);
      setActionError(err?.response?.data?.error || 'Could not update this receipt. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const removeFromBooks = async () => {
    setBusy('removing');
    setActionError(null);
    try {
      const result = await unapproveExpenseDraft(draft.id);
      onReopened(result.draft, 'Removed from your books. Fix the details, then add it again.');
    } catch (err) {
      setConfirmRemove(false);
      setActionError(err?.response?.data?.error || 'Could not remove this from your books. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const moveBackToReview = async () => {
    setBusy('reopening');
    setActionError(null);
    try {
      onReopened(await reopenExpenseDraft(draft.id), 'Moved back to review.');
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Could not update this receipt. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const disabled = locked || Boolean(busy);
  const fieldError = (name) => amountErrors[name] || (approveTried && missing[name]) || null;

  const preview = doc?.previewUrl && (
    <Box>
      {doc.mimeType === 'application/pdf' && fullScreen && (
        <Typography variant="body2" color="text.secondary">
          PDF bill. Use “Open file” to see it.
        </Typography>
      )}
      {doc.mimeType === 'application/pdf' && !fullScreen && (
        <Box
          component="iframe"
          title={doc.fileName || 'Receipt PDF'}
          src={doc.previewUrl}
          sx={{ width: '100%', height: { xs: '45vh', md: '65vh' }, border: 0 }}
        />
      )}
      {doc.mimeType !== 'application/pdf' && BROWSER_PREVIEWABLE.has(doc.mimeType) && (
        <Box
          component="img"
          src={doc.previewUrl}
          alt={doc.fileName || 'Receipt photo'}
          sx={{ display: 'block', maxWidth: '100%', maxHeight: { xs: '40vh', md: '65vh' }, mx: 'auto' }}
        />
      )}
      {!BROWSER_PREVIEWABLE.has(doc.mimeType) && (
        <Typography variant="body2">This browser can’t show this photo type.</Typography>
      )}
      <Button href={doc.previewUrl} target="_blank" rel="noopener noreferrer" size="small" sx={{ mt: 1 }}>
        Open file
      </Button>
    </Box>
  );

  const approvedSummary = locked && draft.reviewStatus === 'approved' && (
    <Alert severity="success">
      Added to your books
      {draft.approvedAt ? ` on ${formatDateTime(draft.approvedAt)}` : ''}
      {category ? ` under ${category.label}` : ''}
      {property ? ` for ${propertyLabel(property)}${unit ? `, ${unitLabel(unit)}` : ''}` : ''}.
    </Alert>
  );

  return (
    <Dialog open={Boolean(review)} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
      <DialogTitle>Review receipt</DialogTitle>
      <DialogContent dividers>
        {(review?.phase === 'loading' || review?.phase === 'reading') && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <CircularProgress aria-label={review.phase === 'reading' ? 'Reading the receipt' : 'Loading'} />
            {review.phase === 'reading' && (
              <Typography variant="body1">Reading the receipt… this takes a few seconds.</Typography>
            )}
          </Stack>
        )}
        {review?.phase === 'error' && <Alert severity="warning">{review.error}</Alert>}

        {ready && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {preview}
            <Stack spacing={2}>
              {approvedSummary}
              {!locked && review.notice && <Alert severity="info">{review.notice}</Alert>}
              {locked && draft.reviewStatus === 'rejected' && (
                <Alert severity="info">{rejectedText(draft)}</Alert>
              )}
              {!locked && failed && (
                <Alert severity="info">{FAILED_TEXT[doc.extractionErrorCode] || FAILED_TEXT.default}</Alert>
              )}
              {!locked && !failed && draft.extraction?.provider === 'openai' && (
                <Typography variant="body2" color="text.secondary">
                  We filled these in from the receipt. Check them against the image and fix anything that’s off.
                </Typography>
              )}
              {!locked &&
                warnings.map((w) => (
                  <Alert key={w} severity="warning">
                    {WARNING_TEXT[w]}
                  </Alert>
                ))}
              {FIELDS.map(({ name, label, type, multiline }) => {
                const hint = locked ? null : confidenceHint(draft, name, values[name]);
                const error = fieldError(name);
                if (name === 'currency') {
                  const options = CURRENCIES.includes(values.currency) || !values.currency
                    ? CURRENCIES
                    : [...CURRENCIES, values.currency];
                  return (
                    <TextField
                      key={name}
                      select
                      label={label}
                      value={values.currency}
                      onChange={set(name)}
                      disabled={disabled}
                      helperText={hint}
                      slotProps={{ formHelperText: hint ? HINT_PROPS : undefined }}
                    >
                      {options.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }
                return (
                  <TextField
                    key={name}
                    label={label}
                    type={type || 'text'}
                    value={values[name]}
                    onChange={set(name)}
                    disabled={disabled}
                    multiline={multiline}
                    minRows={multiline ? 2 : undefined}
                    slotProps={{
                      inputLabel: type === 'date' ? { shrink: true } : undefined,
                      htmlInput: AMOUNT_FIELDS.includes(name) ? { inputMode: 'decimal' } : undefined,
                      formHelperText: hint && !error ? HINT_PROPS : undefined
                    }}
                    error={Boolean(error)}
                    helperText={error || hint}
                  />
                );
              })}
              {!locked && mismatch && (
                <Alert severity="warning">
                  Subtotal plus tax is {money(subtotal + tax, values.currency)}, but the total is{' '}
                  {money(total, values.currency)}.
                </Alert>
              )}

              <Typography variant="subtitle1" component="h3" sx={{ pt: 1 }}>
                Where it goes in your books
              </Typography>
              {optionsError && <Alert severity="warning">{optionsError}</Alert>}
              <TextField
                select
                label="Property"
                value={properties ? values.propertyId : ''}
                onChange={set('propertyId')}
                disabled={disabled || !properties}
                error={Boolean(fieldError('propertyId'))}
                helperText={
                  fieldError('propertyId') ||
                  (properties && properties.length === 0 ? 'Add a property in Properties & Units first.' : null)
                }
              >
                {(properties || []).map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {propertyLabel(p)}
                  </MenuItem>
                ))}
              </TextField>
              {units.length > 0 && (
                <TextField select label="Unit" value={values.unitId} onChange={set('unitId')} disabled={disabled}>
                  <MenuItem value="">Whole property</MenuItem>
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {unitLabel(u)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                select
                label="Category"
                value={categories ? values.categoryId : ''}
                onChange={set('categoryId')}
                disabled={disabled || !categories}
                error={Boolean(fieldError('categoryId'))}
                helperText={fieldError('categoryId') || (category ? `CRA rental form (T776) line ${category.craLine}` : null)}
              >
                {(categories || []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
              {actionError && <Alert severity="error">{actionError}</Alert>}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {ready && !locked && confirmReject && (
          <>
            <Typography variant="body2" sx={{ mr: 'auto' }}>
              Mark as not an expense? It won’t be added to your books.
            </Typography>
            <Button onClick={() => setConfirmReject(false)} disabled={Boolean(busy)}>
              Keep it
            </Button>
            <Button variant="contained" color="warning" onClick={reject} disabled={Boolean(busy)}>
              {busy === 'rejecting' ? 'Saving…' : 'Yes, not an expense'}
            </Button>
          </>
        )}
        {ready && !locked && !confirmReject && (
          <>
            {failed && doc.extractionErrorCode !== 'unsupported_file_type' && (
              <Button onClick={onRetry} disabled={Boolean(busy)}>
                Try reading again
              </Button>
            )}
            <Button onClick={() => setConfirmReject(true)} disabled={Boolean(busy)} sx={{ mr: 'auto' }}>
              Not an expense
            </Button>
            <Button onClick={onClose} disabled={Boolean(busy)}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={save} disabled={Boolean(busy)}>
              {busy === 'saving' ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="contained" onClick={approve} disabled={Boolean(busy)}>
              {busy === 'approving' ? 'Adding…' : 'Add to my books'}
            </Button>
          </>
        )}
        {locked && draft.reviewStatus === 'approved' && confirmRemove && (
          <>
            <Typography variant="body2" sx={{ mr: 'auto' }}>
              Remove this expense from your books? You can fix the receipt and add it again.
            </Typography>
            <Button onClick={() => setConfirmRemove(false)} disabled={Boolean(busy)}>
              Keep it
            </Button>
            <Button variant="contained" color="warning" onClick={removeFromBooks} disabled={Boolean(busy)}>
              {busy === 'removing' ? 'Removing…' : 'Yes, remove it'}
            </Button>
          </>
        )}
        {locked && draft.reviewStatus === 'approved' && !confirmRemove && onReopened && (
          <Button onClick={() => setConfirmRemove(true)} sx={{ mr: 'auto' }}>
            Remove from my books
          </Button>
        )}
        {locked && draft.reviewStatus === 'rejected' && !draft.filePurgedAt && onReopened && (
          <Button onClick={moveBackToReview} disabled={Boolean(busy)} sx={{ mr: 'auto' }}>
            {busy === 'reopening' ? 'Moving…' : 'Move back to review'}
          </Button>
        )}
        {(!ready || (locked && !confirmRemove)) && (
          <Button onClick={onClose} disabled={Boolean(busy)}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ExpenseDraftReview;
